import React, { useState } from "react";
import CheckInDetails from "./modules/CheckInDetails";
import CheckInList from "./modules/CheckInList";
import { CheckInItem } from 'types';
import CheckInForm from "./modules/CheckInForm";
import styles from './CheckIn.module.scss';
import { store } from "stores/Store";
import useExternalApiService from "helpers/ExternalApiService";
  

enum Tab {
  CHECK_IN = "check_in",
  DETAILS = "details",
  FORM = "form"
}

interface Props {
  setIsHeaderHidden: (isHidden: boolean) => void;
  onCheckinAdded?: () => void;
}


const CheckIn = ({setIsHeaderHidden, onCheckinAdded}: Props) => {
  const [checkIns, setCheckIns] = React.useState<CheckInItem[]>();
  const [selectedTab, setSelectedTab] = React.useState<Tab>(Tab.CHECK_IN);
  const [selectedCheckIn, setSelectedCheckIn] = React.useState<CheckInItem | null>(null);
  const [selectedAddress, setSelectedAddress] = useState();

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);

  const navigateToCheckInList = () => {
    setCheckIns(undefined);
    setSelectedTab(Tab.CHECK_IN);
    store.clearCheckIn();
    getCheckIns();
  }

  React.useEffect(() => {
    // Don't open up check-ins automatiically.
    /*
    if(store.checkIn) {
      setSelectedTab(Tab.FORM);
    }*/

    getCheckIns();
  }, []);

  const getCheckIns = () => {
    const getCheckIns = async () => {
      try {
        const practitionerId = store.currentPractitioner?.id ?? '';
        const netsuiteId = store.currentPractitioner?.netsuite_id ?? '';
        const bg_rowid = store.currentPractitioner?.bg_rowid ?? '';

        // Always fetch this with error handling
        let activeResult;
        try {
          activeResult = await postData('/api/get_checkins_for_practitioner', {practitioner_id: practitionerId});
        } catch (error) {
          console.error('Error fetching active check-ins:', error);
          activeResult = { checkins: [] };
        }

        // Only fetch archived if we have a proper netsuite ID
        let archivedResult;
        if (netsuiteId) {
          try {
            archivedResult = await postData('/api/get_checkins_for_practitioner_archived', { internal_practitioner_netsuite_id: netsuiteId });
          } catch (error) {
            console.error('Error fetching archived check-ins:', error);
            archivedResult = { checkins: [] };
          }
        } else {
          archivedResult = { checkins: [] };
        }

        // Only fetch archivedBGResult if bg_rowid exists
        let archivedBGResult;
        if (bg_rowid) {
          try {
            archivedBGResult = await postData('/api/get_checkins_for_practitioner_archived_bg', {badger_account_rowid: bg_rowid});
          } catch (error) {
            console.error('Error fetching archived BG check-ins:', error);
            archivedBGResult = { checkins: [] };
          }
        } else {
          archivedBGResult = { checkins: [] };
        }

        // Safely extract check-ins arrays with fallbacks
        const activeCheckIns = activeResult?.checkins || [];
        const archivedCheckInsRaw = archivedResult?.checkins || [];
        const archivedBGCheckInsRaw = archivedBGResult?.checkins || [];

        // Tag archived check-ins with safe array handling
        const archivedCheckIns = Array.isArray(archivedCheckInsRaw)
          ? archivedCheckInsRaw.map((ci: CheckInItem) => ({
              ...ci,
              is_archived: true
            }))
          : [];

        const archivedBGCheckIns = Array.isArray(archivedBGCheckInsRaw)
          ? archivedBGCheckInsRaw.map((ci: CheckInItem) => ({
              ...ci,
              is_archived_bg: true
            }))
          : [];

        // Combine all check-ins from the three APIs with safe spreading
        const allCheckIns = [
          ...(Array.isArray(activeCheckIns) ? activeCheckIns : []),
          ...archivedCheckIns,
          ...archivedBGCheckIns
        ];

        // Sort ALL check-ins together by created_at (most recent first)
        // Safari-compatible date sorting using the same normalization as helpers
        const sortedAllCheckIns = allCheckIns.sort((a, b) => {
          // Handle missing created_at fields
          if (!a.created_at && !b.created_at) return 0;
          if (!a.created_at) return 1; // a goes to end
          if (!b.created_at) return -1; // b goes to end

          // Normalize date strings for Safari compatibility (same as helpers/index.ts)
          const normalizeDateString = (input: string) => {
            return input.replace(" ", "T").replace(" Z", "Z");
          };

          try {
            // Parse dates with Safari-compatible normalization
            const dateA = new Date(normalizeDateString(a.created_at));
            const dateB = new Date(normalizeDateString(b.created_at));

            // Handle invalid dates (Safari returns NaN for invalid dates)
            const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
            const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();

            // Sort most recent first
            return timeB - timeA;
          } catch (error) {
            console.error('Date parsing error in sort:', error);
            // Fallback: try string comparison
            return b.created_at.localeCompare(a.created_at);
          }
        });

        setCheckIns(sortedAllCheckIns);
      } catch (error) {
        console.error('Error in getCheckIns:', error);
        // Set empty array as fallback
        setCheckIns([]);
      }
    }

    getCheckIns();
  }

  const handleCheckInClick = (checkIn: CheckInItem) => {
    setSelectedCheckIn(checkIn);
    setSelectedTab(Tab.DETAILS);
  }

  const handleCheckInUpdate = (updatedCheckIn: CheckInItem) => {
    // Update the selected check-in
    setSelectedCheckIn(updatedCheckIn);
    
    // Update the check-ins list
    if (checkIns) {
      const updatedCheckIns = checkIns.map(ci => 
        ci.id === updatedCheckIn.id ? updatedCheckIn : ci
      );
      setCheckIns(updatedCheckIns);
    }
  }

  React.useEffect(() => {
    setIsHeaderHidden(selectedTab === Tab.FORM);
  }, [selectedTab, setIsHeaderHidden]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case Tab.CHECK_IN:
        return <CheckInList 
          onSelectedAddressChange={(a) => setSelectedAddress(a)} 
          onCheckInClick={handleCheckInClick} checkIns={checkIns} addNewCheckIn={() => setSelectedTab(Tab.FORM)}/>
        case Tab.FORM:
          return <CheckInForm navigateBack={navigateToCheckInList} selectedAddress={selectedAddress} onCheckinAdded={onCheckinAdded} />
      case Tab.DETAILS:
        if(selectedCheckIn) {
          return <CheckInDetails 
            checkIn={selectedCheckIn} 
            returnToCheckInList={() => setSelectedTab(Tab.CHECK_IN)}
            onCheckInUpdate={handleCheckInUpdate}
          />
        }
        return <p>No check-in selected</p>
    }
  }
  
  return (
    <div className={styles.wrapper}>
      {renderTabContent()}
    </div>
  );
};

export default CheckIn;